import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { format, startOfDay, addDays, differenceInDays } from "date-fns";
import type { CgmReading, LogEntry } from "../types";

export type GraphMode = "straight" | "overlay";

export interface GraphMarker {
  timestamp: Date;
  type: "bg" | "insulin" | "carbs" | "exercise" | "sick";
  value?: number;
  label?: string;
}

// Colors for overlay weeks
const OVERLAY_COLORS = [
  { line: "#0d6efd", fill: "rgba(13, 110, 253, 0.15)" }, // Blue - Week 1 (most recent)
  { line: "#6f42c1", fill: "rgba(111, 66, 193, 0.15)" }, // Purple - Week 2
  { line: "#20c997", fill: "rgba(32, 201, 151, 0.15)" }, // Teal - Week 3
  { line: "#fd7e14", fill: "rgba(253, 126, 20, 0.15)" }, // Orange - Week 4
];

@customElement("cgm-graph")
export class CgmGraph extends LitElement {
  @property({ type: Array }) readings: CgmReading[] = [];
  @property({ type: Array }) entries: LogEntry[] = [];
  @property({ type: Object }) dateRange: { start: Date; end: Date } | null =
    null;
  @property({ type: Number }) targetLow = 4.0; // mmol/L
  @property({ type: Number }) targetHigh = 10.0; // mmol/L
  @property({ type: Number }) height = 200;
  @property({ type: Boolean }) showMarkers = true;
  @property({ type: Boolean }) hasAnyData = false; // Whether CGM data exists in DB (for any date)
  @property({ type: String }) mode: GraphMode = "straight"; // straight or overlay

  @state() private hoveredPoint: {
    reading: CgmReading;
    x: number;
    y: number;
  } | null = null;

  static styles = css`
    :host {
      display: block;
      position: relative;
    }

    .graph-container {
      position: relative;
      background: #fff;
      border-radius: 0.5rem;
      overflow: hidden;
    }

    canvas {
      display: block;
      width: 100%;
    }

    .no-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.5rem;
      text-align: center;
    }

    .no-data sl-icon {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }

    .no-data a {
      color: #0d6efd;
      text-decoration: none;
    }

    .tooltip {
      position: absolute;
      background: rgba(33, 37, 41, 0.95);
      color: white;
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      pointer-events: none;
      z-index: 10;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    .tooltip .value {
      font-size: 1rem;
      font-weight: 600;
    }

    .tooltip .time {
      color: #adb5bd;
    }

    .legend {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      padding: 0.75rem;
      background: #f8f9fa;
      border-top: 1px solid #dee2e6;
      font-size: 0.75rem;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }

    .legend-line {
      width: 16px;
      height: 3px;
      border-radius: 2px;
    }

    .target-range {
      background: rgba(25, 135, 84, 0.2);
    }

    .glucose-line {
      background: #0d6efd;
    }

    .marker-bg {
      background: #dc3545;
    }

    .marker-insulin {
      background: #6f42c1;
    }

    .marker-carbs {
      background: #fd7e14;
    }

    .y-axis {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 35px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 10px 0;
      font-size: 0.625rem;
      color: #6c757d;
      pointer-events: none;
    }

    .y-axis span {
      text-align: right;
      padding-right: 4px;
    }

    .x-axis {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 35px 0.25rem 35px;
      font-size: 0.625rem;
      color: #6c757d;
      background: #f8f9fa;
    }
  `;

  updated(changedProperties: Map<string, unknown>) {
    if (
      changedProperties.has("readings") ||
      changedProperties.has("entries") ||
      changedProperties.has("dateRange") ||
      changedProperties.has("height") ||
      changedProperties.has("mode")
    ) {
      this._drawGraph();
    }
  }

  // Helper to safely get timestamp as milliseconds (handles Date or string)
  private _getTs(timestamp: Date | string): number {
    return timestamp instanceof Date
      ? timestamp.getTime()
      : new Date(timestamp).getTime();
  }

  firstUpdated() {
    this._drawGraph();

    // Add resize observer
    const resizeObserver = new ResizeObserver(() => {
      this._drawGraph();
    });

    const container = this.shadowRoot?.querySelector(".graph-container");
    if (container) {
      resizeObserver.observe(container);
    }
  }

  private _getFilteredReadings(): CgmReading[] {
    if (!this.dateRange) return this.readings;

    const startTime = this.dateRange.start.getTime();
    const endTime = this.dateRange.end.getTime();

    return this.readings.filter((r) => {
      const ts = this._getTs(r.timestamp);
      return ts >= startTime && ts <= endTime;
    });
  }

  private _getMarkers(): GraphMarker[] {
    if (!this.showMarkers || !this.dateRange) return [];

    const markers: GraphMarker[] = [];
    const startTime = this.dateRange.start.getTime();
    const endTime = this.dateRange.end.getTime();

    const filteredEntries = this.entries.filter((e) => {
      const ts = this._getTs(e.timestamp);
      return ts >= startTime && ts <= endTime;
    });

    for (const entry of filteredEntries) {
      // BG reading marker
      if (entry.bloodGlucose !== undefined) {
        markers.push({
          timestamp: entry.timestamp,
          type: "bg",
          value: entry.bloodGlucose,
          label: `BG: ${entry.bloodGlucose}`,
        });
      }

      // Insulin marker
      const qaTotal = (entry.qaBase || 0) + (entry.qaCorrection || 0);
      const biTotal = (entry.biBase || 0) + (entry.biCorrection || 0);
      if (qaTotal > 0 || biTotal > 0) {
        markers.push({
          timestamp: entry.timestamp,
          type: "insulin",
          value: qaTotal || biTotal,
          label: qaTotal > 0 ? `QA: ${qaTotal}u` : `BI: ${biTotal}u`,
        });
      }

      // Carbs marker
      if (entry.carbs && entry.carbs > 0) {
        markers.push({
          timestamp: entry.timestamp,
          type: "carbs",
          value: entry.carbs,
          label: `${entry.carbs}g`,
        });
      }

      // Exercise marker
      if (entry.exercise) {
        markers.push({
          timestamp: entry.timestamp,
          type: "exercise",
          label: `${entry.exercise.duration}min ${entry.exercise.intensity}`,
        });
      }

      // Sick day marker
      if (entry.sickDay) {
        markers.push({
          timestamp: entry.timestamp,
          type: "sick",
          label: entry.sickDay.status === "sick" ? "🤒 Sick" : "😊 Better",
        });
      }
    }

    return markers;
  }

  private _drawGraph() {
    const canvas = this.shadowRoot?.querySelector(
      "canvas",
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const container = this.shadowRoot?.querySelector(
      ".graph-container",
    ) as HTMLElement;
    if (!container) return;

    const readings = this._getFilteredReadings();
    const markers = this._getMarkers();

    // Set canvas dimensions
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const width = rect.width;

    canvas.width = width * dpr;
    canvas.height = this.height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${this.height}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, width, this.height);

    // Padding for axes
    const padding = { top: 10, right: 10, bottom: 10, left: 35 };
    const graphWidth = width - padding.left - padding.right;
    const graphHeight = this.height - padding.top - padding.bottom;

    if (readings.length === 0) return;

    // Calculate scales
    const minGlucose = 2;
    const maxGlucose = 20;
    const glucoseRange = maxGlucose - minGlucose;

    const yScale = (glucose: number) =>
      padding.top + ((maxGlucose - glucose) / glucoseRange) * graphHeight;

    // Draw target range (4-10 mmol/L)
    ctx.fillStyle = "rgba(25, 135, 84, 0.1)";
    const targetTop = yScale(this.targetHigh);
    const targetBottom = yScale(this.targetLow);
    ctx.fillRect(padding.left, targetTop, graphWidth, targetBottom - targetTop);

    // Draw target range lines
    ctx.strokeStyle = "rgba(25, 135, 84, 0.3)";
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(padding.left, targetTop);
    ctx.lineTo(width - padding.right, targetTop);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(padding.left, targetBottom);
    ctx.lineTo(width - padding.right, targetBottom);
    ctx.stroke();

    ctx.setLineDash([]);

    // Use overlay mode or straight mode
    if (this.mode === "overlay" && this.dateRange) {
      this._drawOverlayMode(
        ctx,
        readings,
        padding,
        graphWidth,
        graphHeight,
        minGlucose,
        maxGlucose,
        yScale,
      );
    } else {
      this._drawStraightMode(
        ctx,
        readings,
        markers,
        padding,
        graphWidth,
        minGlucose,
        maxGlucose,
        yScale,
        width,
      );
    }
  }

  private _drawStraightMode(
    ctx: CanvasRenderingContext2D,
    readings: CgmReading[],
    markers: GraphMarker[],
    padding: { top: number; right: number; bottom: number; left: number },
    graphWidth: number,
    minGlucose: number,
    maxGlucose: number,
    yScale: (g: number) => number,
    _width: number,
  ) {
    const timeMin = this.dateRange
      ? this.dateRange.start.getTime()
      : Math.min(...readings.map((r) => this._getTs(r.timestamp)));
    const timeMax = this.dateRange
      ? this.dateRange.end.getTime()
      : Math.max(...readings.map((r) => this._getTs(r.timestamp)));
    const timeRange = timeMax - timeMin || 1;

    const xScale = (time: number) =>
      padding.left + ((time - timeMin) / timeRange) * graphWidth;

    // Sort readings by timestamp
    const sortedReadings = [...readings].sort(
      (a, b) => this._getTs(a.timestamp) - this._getTs(b.timestamp),
    );

    // Draw glucose line
    if (sortedReadings.length > 1) {
      // Draw area under curve
      ctx.beginPath();
      ctx.moveTo(
        xScale(this._getTs(sortedReadings[0].timestamp)),
        yScale(minGlucose),
      );

      for (const reading of sortedReadings) {
        const x = xScale(this._getTs(reading.timestamp));
        const y = yScale(
          Math.min(Math.max(reading.glucose, minGlucose), maxGlucose),
        );
        ctx.lineTo(x, y);
      }

      ctx.lineTo(
        xScale(
          this._getTs(sortedReadings[sortedReadings.length - 1].timestamp),
        ),
        yScale(minGlucose),
      );
      ctx.closePath();

      const gradient = ctx.createLinearGradient(0, 0, 0, this.height);
      gradient.addColorStop(0, "rgba(13, 110, 253, 0.3)");
      gradient.addColorStop(1, "rgba(13, 110, 253, 0.05)");
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw line
      ctx.beginPath();
      ctx.strokeStyle = "#0d6efd";
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";

      for (let i = 0; i < sortedReadings.length; i++) {
        const reading = sortedReadings[i];
        const x = xScale(this._getTs(reading.timestamp));
        const y = yScale(
          Math.min(Math.max(reading.glucose, minGlucose), maxGlucose),
        );

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    }

    // Draw markers
    for (const marker of markers) {
      const x = xScale(this._getTs(marker.timestamp));

      // Marker colors by type
      const colors: Record<string, string> = {
        bg: "#dc3545",
        insulin: "#6f42c1",
        carbs: "#fd7e14",
        exercise: "#20c997",
        sick: "#dc3545",
      };

      const color = colors[marker.type] || "#6c757d";

      // Draw vertical line
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, this.height - padding.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw marker dot at top
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, padding.top + 5, 4, 0, Math.PI * 2);
      ctx.fill();

      // If it's a BG reading, draw at the correct glucose level too
      if (marker.type === "bg" && marker.value !== undefined) {
        const y = yScale(
          Math.min(Math.max(marker.value, minGlucose), maxGlucose),
        );
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();

        // White inner circle
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private _drawOverlayMode(
    ctx: CanvasRenderingContext2D,
    readings: CgmReading[],
    padding: { top: number; right: number; bottom: number; left: number },
    graphWidth: number,
    _graphHeight: number,
    minGlucose: number,
    maxGlucose: number,
    yScale: (g: number) => number,
  ) {
    if (!this.dateRange) return;

    // Calculate number of weeks in the range
    const totalDays =
      differenceInDays(this.dateRange.end, this.dateRange.start) + 1;
    const numWeeks = Math.ceil(totalDays / 7);

    // X-axis is 7 days (one week)
    const msPerDay = 24 * 60 * 60 * 1000;

    const xScale = (dayOffset: number, timeOfDay: number) => {
      // dayOffset is 0-6 (Mon-Sun), timeOfDay is ms from midnight
      const pos = dayOffset + timeOfDay / msPerDay;
      return padding.left + (pos / 7) * graphWidth;
    };

    // Group readings by week (most recent week first)
    const weeklyReadings: CgmReading[][] = [];
    const endDate = startOfDay(this.dateRange.end);

    for (let week = 0; week < numWeeks; week++) {
      const weekEnd = addDays(endDate, -week * 7);
      const weekStart = addDays(weekEnd, -6);
      const weekStartTs = weekStart.getTime();
      const weekEndTs = addDays(weekEnd, 1).getTime(); // End of day

      const weekReadings = readings.filter((r) => {
        const ts = this._getTs(r.timestamp);
        return ts >= weekStartTs && ts < weekEndTs;
      });

      if (weekReadings.length > 0) {
        weeklyReadings.push(weekReadings);
      }
    }

    // Draw each week with different colors (oldest first so newest is on top)
    for (let i = weeklyReadings.length - 1; i >= 0; i--) {
      const weekReadings = weeklyReadings[i];
      const colors =
        OVERLAY_COLORS[i] || OVERLAY_COLORS[OVERLAY_COLORS.length - 1];

      // Sort readings by time of day within week
      const sortedReadings = [...weekReadings].sort(
        (a, b) => this._getTs(a.timestamp) - this._getTs(b.timestamp),
      );

      // Map readings to week-relative coordinates
      const mappedReadings = sortedReadings.map((r) => {
        const ts = new Date(this._getTs(r.timestamp));
        const dayOfWeek = (ts.getDay() + 6) % 7; // Monday = 0, Sunday = 6
        const midnight = startOfDay(ts).getTime();
        const timeOfDay = ts.getTime() - midnight;
        return { reading: r, dayOfWeek, timeOfDay };
      });

      if (mappedReadings.length < 2) continue;

      // Draw line (no fill in overlay mode for clarity)
      ctx.beginPath();
      ctx.strokeStyle = colors.line;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.globalAlpha = 0.8;

      for (let j = 0; j < mappedReadings.length; j++) {
        const { reading, dayOfWeek, timeOfDay } = mappedReadings[j];
        const x = xScale(dayOfWeek, timeOfDay);
        const y = yScale(
          Math.min(Math.max(reading.glucose, minGlucose), maxGlucose),
        );

        if (j === 0) {
          ctx.moveTo(x, y);
        } else {
          // Check for day boundary breaks (don't connect across large gaps)
          const prev = mappedReadings[j - 1];
          const timeDiff =
            this._getTs(reading.timestamp) -
            this._getTs(prev.reading.timestamp);
          if (timeDiff > 30 * 60 * 1000) {
            // 30 min gap = break
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  // Get week number for overlay legend
  getOverlayWeeks(): number {
    if (!this.dateRange) return 0;
    const totalDays =
      differenceInDays(this.dateRange.end, this.dateRange.start) + 1;
    return Math.min(Math.ceil(totalDays / 7), OVERLAY_COLORS.length);
  }

  private _handleMouseMove(e: MouseEvent) {
    const canvas = this.shadowRoot?.querySelector(
      "canvas",
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const readings = this._getFilteredReadings();
    if (readings.length === 0) return;

    // Find closest reading
    const padding = { left: 35, right: 10 };
    const graphWidth = rect.width - padding.left - padding.right;

    const timeMin = this.dateRange
      ? this.dateRange.start.getTime()
      : Math.min(...readings.map((r) => this._getTs(r.timestamp)));
    const timeMax = this.dateRange
      ? this.dateRange.end.getTime()
      : Math.max(...readings.map((r) => this._getTs(r.timestamp)));
    const timeRange = timeMax - timeMin || 1;

    // Convert mouse x to time
    const mouseTime = timeMin + ((x - padding.left) / graphWidth) * timeRange;

    // Find closest reading
    let closest: CgmReading | null = null;
    let closestDist = Infinity;

    for (const reading of readings) {
      const dist = Math.abs(this._getTs(reading.timestamp) - mouseTime);
      if (dist < closestDist) {
        closestDist = dist;
        closest = reading;
      }
    }

    if (closest && closestDist < timeRange / 20) {
      const readingX =
        padding.left +
        ((this._getTs(closest.timestamp) - timeMin) / timeRange) * graphWidth;
      const yScale = (glucose: number) =>
        10 + ((20 - glucose) / 18) * (this.height - 20);
      const readingY = yScale(Math.min(Math.max(closest.glucose, 2), 20));

      this.hoveredPoint = { reading: closest, x: readingX, y: readingY };
    } else {
      this.hoveredPoint = null;
    }
  }

  private _handleMouseLeave() {
    this.hoveredPoint = null;
  }

  private _getXAxisLabels(): string[] {
    if (!this.dateRange) return [];

    // For overlay mode, show day of week labels
    if (this.mode === "overlay") {
      return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    }

    // For straight mode, show date/time labels
    const start = this.dateRange.start;
    const end = this.dateRange.end;
    const range = end.getTime() - start.getTime();
    const hours = range / (1000 * 60 * 60);

    const labels: string[] = [];

    if (hours <= 24) {
      // Show hours for single day
      for (let i = 0; i <= 4; i++) {
        const time = new Date(start.getTime() + (range * i) / 4);
        labels.push(format(time, "HH:mm"));
      }
    } else {
      // Show days for week view
      const days = Math.ceil(hours / 24);
      const step = Math.max(1, Math.floor(days / 5));
      for (let i = 0; i <= days; i += step) {
        const time = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
        labels.push(format(time, "dd MMM"));
      }
    }

    return labels;
  }

  private _getOverlayLegend() {
    if (!this.dateRange) return "";

    const totalDays =
      differenceInDays(this.dateRange.end, this.dateRange.start) + 1;
    const numWeeks = Math.min(Math.ceil(totalDays / 7), OVERLAY_COLORS.length);

    const weekLabels = [];
    for (let i = 0; i < numWeeks; i++) {
      const color = OVERLAY_COLORS[i];
      const weekNum = i + 1;
      weekLabels.push(html`
        <div class="legend-item">
          <div class="legend-line" style="background: ${color.line}"></div>
          <span>Week ${weekNum}</span>
        </div>
      `);
    }
    return weekLabels;
  }

  render() {
    const readings = this._getFilteredReadings();

    if (readings.length === 0) {
      return html`
        <div class="no-data">
          <sl-icon name="graph-up"></sl-icon>
          ${this.hasAnyData
            ? html`<div>No readings for this day</div>`
            : html`
                <div>No CGM data available</div>
                <div><a href="/upload">Import LibreView data</a></div>
              `}
        </div>
      `;
    }

    const xLabels = this._getXAxisLabels();

    return html`
      <div class="graph-container">
        <div class="y-axis">
          <span>20</span>
          <span>15</span>
          <span>10</span>
          <span>5</span>
          <span>2</span>
        </div>
        <canvas
          @mousemove=${this._handleMouseMove}
          @mouseleave=${this._handleMouseLeave}
        ></canvas>
        ${this.hoveredPoint
          ? html`
              <div
                class="tooltip"
                style="left: ${this.hoveredPoint.x + 10}px; top: ${this
                  .hoveredPoint.y - 40}px;"
              >
                <div class="value">
                  ${this.hoveredPoint.reading.glucose.toFixed(1)} mmol/L
                </div>
                <div class="time">
                  ${format(
                    new Date(this.hoveredPoint.reading.timestamp),
                    "HH:mm",
                  )}
                </div>
              </div>
            `
          : ""}
      </div>
      ${xLabels.length > 0
        ? html`
            <div class="x-axis">
              ${xLabels.map((label) => html`<span>${label}</span>`)}
            </div>
          `
        : ""}
      ${this.showMarkers && this.mode === "straight"
        ? html`
            <div class="legend">
              <div class="legend-item">
                <div class="legend-line glucose-line"></div>
                <span>CGM</span>
              </div>
              <div class="legend-item">
                <div class="legend-line target-range"></div>
                <span>Target (${this.targetLow}-${this.targetHigh})</span>
              </div>
              <div class="legend-item">
                <div class="legend-dot marker-bg"></div>
                <span>BG Test</span>
              </div>
              <div class="legend-item">
                <div class="legend-dot marker-insulin"></div>
                <span>Insulin</span>
              </div>
              <div class="legend-item">
                <div class="legend-dot marker-carbs"></div>
                <span>Carbs</span>
              </div>
            </div>
          `
        : ""}
      ${this.mode === "overlay"
        ? html`
            <div class="legend">
              ${this._getOverlayLegend()}
              <div class="legend-item">
                <div class="legend-line target-range"></div>
                <span>Target</span>
              </div>
            </div>
          `
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cgm-graph": CgmGraph;
  }
}
